/* eslint-disable react/jsx-filename-extension */
import React, { createContext, useReducer } from 'react';
import dayjs from 'dayjs';
import { set } from 'dot-prop-immutable';

const parseLocalStorageJson = (key, value) => {
    if (!value || value === 'undefined') {
        window.localStorage.removeItem(key);
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        window.localStorage.removeItem(key);
        return null;
    }
};

const setLocalStorageJson = (key, value) => {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
        window.localStorage.removeItem(key);
        return;
    }
    window.localStorage.setItem(key, serialized);
};

const getInitialData = () => {
    const localUserData = window.localStorage.getItem('user');
    const localTeamData = window.localStorage.getItem('team');
    const localOpponentTeamData = window.localStorage.getItem('opponent');
    const localMatchupPitcher = window.localStorage.getItem('matchupSelectedPitcher');
    const localConfigData = window.localStorage.getItem('config');
    const localPlayerSettings = window.localStorage.getItem('playerSettings');
    const expiresOn = window.localStorage.getItem('expiration');

    let user = null;
    let config = null;
    let playerSettings = null;
    let team = null;
    let opponent = null;
    let matchupSelectedPitcher = null;
    try {
        if (localUserData && expiresOn && dayjs().isBefore(expiresOn)) {
            user = parseLocalStorageJson('user', localUserData);
            config = parseLocalStorageJson('config', localConfigData);
        }
    } finally {
        if (!user) {
            window.localStorage.removeItem('user');
            window.localStorage.removeItem('config');
            window.localStorage.removeItem('expiration');
        }
    }

    try {
        if (localTeamData && localTeamData !== undefined) {
            team = parseLocalStorageJson('team', localTeamData);
        }
    } finally {
        if (!team) {
            window.localStorage.removeItem('team');
        }
    }

    try {
        if (localOpponentTeamData && localOpponentTeamData !== undefined) {
            opponent = parseLocalStorageJson('opponent', localOpponentTeamData);
        }
    } finally {
        if (!opponent) {
            window.localStorage.removeItem('opponent');
        }
    }

    try {
        if (localMatchupPitcher && localMatchupPitcher !== undefined) {
            matchupSelectedPitcher = parseLocalStorageJson('matchupSelectedPitcher', localMatchupPitcher);
        }
    } finally {
        if (!matchupSelectedPitcher) window.localStorage.removeItem('matchupSelectedPitcher');
    }

    try {
        if (localPlayerSettings && localPlayerSettings !== undefined) {
            playerSettings = parseLocalStorageJson('playerSettings', localPlayerSettings);
        }
    } finally {
        if (!playerSettings) {
            window.localStorage.removeItem('playerSettings');
        }
    }

    return { user, config, playerSettings, team, opponent, matchupSelectedPitcher };
};

const setDugoutInitialContext = (user, team, opponent) => {
    const { user: storedUser, team: storedTeam, opponent: storedOpponent } = getInitialData();
    if (!storedUser) {
        setLocalStorageJson('user', user);
        window.localStorage.setItem('expiration', dayjs().add(1, 'h').toISOString());
    }
    if (!storedTeam) {
        setLocalStorageJson('team', team);
    }
    if (!storedOpponent && opponent) {
        setLocalStorageJson('opponent', opponent);
    }
};

const initialState = getInitialData();
const store = createContext(initialState);
const { Provider } = store;

const actions = {
    LOG_IN: 'LOGIN',
    LOG_OUT: 'LOGOUT',
    SELECT_ALIAS: 'SELECTALIAS',
    ASSIGN_USER_PLAYER: 'ASSIGNUSERPLAYER',
    SET_CONFIG: 'SETCONFIG',
    SET_PLAYER_SETTINGS: 'SETPLAYERSETTINGS',
    SET_PLAYER_RMB: 'SETPLAYERRMB',
    SET_TEAM: 'SETTEAM',
    SET_OPPONENT: 'SETOPPNENT',
    SET_MATCHUP_SELECTED_PITCHER: 'SETMATCHUPPITCHER',
};

const reducer = (state, { type, data }) => {
    switch (type) {
    case actions.LOG_IN: {
        if (data?.UserName && state?.user?.UserName !== data?.UserName) {
            fetch('/api/usagetracking', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Host: window.location.hostname,
                    Type: 'login',
                    UserName: data?.UserName,
                }),
            });
        }
        return set(state, 'user', (user) => ({ ...user, ...data }));
    }
    case actions.LOG_OUT: {
        return set(set(state, 'playerSettings', null), 'user', null);
    }
    case actions.SELECT_ALIAS: {
        return set(set(state, 'playerSettings', null), 'user.alias', data);
    }
    case actions.ASSIGN_USER_PLAYER: {
        return set(state, 'user.player', data);
    }
    case actions.SET_CONFIG: {
        return set(state, 'config', data);
    }
    case actions.SET_PLAYER_SETTINGS: {
        return set(state, 'playerSettings', data);
    }
    case actions.SET_PLAYER_RMB: {
        return set(state, 'playerRmb', data); // roster management board
    }
    case actions.SET_TEAM: {
        return set(state, 'team', { ...data });
    }
    case actions.SET_OPPONENT: {
        return set(state, 'opponent', { ...data });
    }
    case actions.SET_MATCHUP_SELECTED_PITCHER: {
        return set(state, 'matchupSelectedPitcher', { ...data });
    }
    default: {
        return state;
    }
    }
};

// Save changes to local storage
const middleware = (reduce) => (state, action) => {
    const result = reduce(state, action);
    try {
        setLocalStorageJson('user', result.user);
        setLocalStorageJson('team', result.team);
        setLocalStorageJson('opponent', result.opponent);
        setLocalStorageJson('matchupSelectedPitcher', result.matchupSelectedPitcher);
        setLocalStorageJson('config', result.config);
        setLocalStorageJson('playerSettings', result.playerSettings);
        setLocalStorageJson('playerRmb', result.playerRmb);
        window.localStorage.setItem('expiration', dayjs().add(12, 'h').toISOString());
    } catch { /* silently fail */ }
    return result;
};

// eslint-disable-next-line react/prop-types
const PlayerPortalProvider = ({ children }) => {
    const [state, dispatch] = useReducer(middleware(reducer), initialState);
    return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

// eslint-disable-next-line react/prop-types
const DugoutProvider = ({ User, Team, children }) => {
    const dugoutInitialState = {
        ...initialState,
        user: initialState.user || User,
        team: Team,
    };
    const [state, dispatch] = useReducer(middleware(reducer), dugoutInitialState);
    return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

export {
    store, actions, PlayerPortalProvider, DugoutProvider, setDugoutInitialContext,
};
